<script lang="ts">
	import { format } from 'svelte-i18n';
	import { Container, Text, Checkbox, ListItem, Stack } from '@Shelly/shelly-design-system';
		import { createMemberSettingsSnapshot, readDeviceStatus } from 'src/stores/device-runtime/status-selectors';
import { devices } from 'src/stores/lists/devices';
	import { isShellyPresence, parseAlarmDeviceRr, real_id } from 'src/helpers/interface_helpers';
	import { getPresenceZones } from 'src/components/Scenes/helpers/device_conditions/custom_cases/presence/presence-device-helpers';
	import {
		buildPresenceConditionName,
		sceneZoneIdToDeviceZoneId,
	} from 'src/components/Scenes/SceneWizard/Process/When/AddEditContionWizard/Steps/DeviceBased/helpers/presence-helpers';
	import { getSettings } from 'src/helpers/get_status_settings_helpers';
	import type { IAlarmZone } from '../alarm_object';

	export let alarm_object: IAlarmZone;
	/** Selected Shelly presence devices from the Devices step (merged device records). */
	export let selectedPresenceSensors: any[] = [];

	const emptyCheckedSet = new Set<string>();

	const sensorIdToDecimal = (sensorId: string): string => {
		if (!sensorId) return '';
		return sensorId.toLowerCase().startsWith('x') ? sensorId : String(parseInt(sensorId, 16));
	};

	$: hasAnyPresence = selectedPresenceSensors.length > 0;

	$: selectedPresenceDecimals = selectedPresenceSensors
		.map((s) => sensorIdToDecimal(s?.id))
		.filter(Boolean);

	$: memberSettingsStore = createMemberSettingsSnapshot(
		selectedPresenceSensors.map((s) => s?.id).filter(Boolean),
	);
	$: memberSettings = $memberSettingsStore;

	$: for (const sensor of selectedPresenceSensors) {
		const sid = sensor?.id;
		if (sid && isShellyPresence($devices[sid]?.type) && !memberSettings[real_id(sid)]) {
			getSettings($devices[sid] ?? sensor);
		}
	}

	let checkedZonesByDevice: Record<string, Set<string>> = {};
	let userDeselectedAllByDevice: Record<string, boolean> = {};
	let presenceDecimalsInitKey = '';

	const readAlarmZones = (decimal: string): Set<string> => {
		const ids = new Set<string>();
		if (!decimal) return ids;
		for (const entry of alarm_object?.if?.or ?? []) {
			const parsed = parseAlarmDeviceRr(entry?.eq?.[0]?.rr);
			if (!parsed || parsed.decimal !== decimal) continue;
			const m = parsed.component?.match(/^prsn_st4(\d+)$/);
			if (m) ids.add(sceneZoneIdToDeviceZoneId(m[1]));
		}
		return ids;
	};

	const anchorRr = (decimal: string) => `dev:${decimal}:presence`;

	const hasAnchorCondition = (decimal: string) =>
		(alarm_object?.if?.or ?? []).some((e) => e?.eq?.[0]?.rr === anchorRr(decimal));

	/**
	 * Strip and rewrite all `if.or` entries for every selected presence device,
	 * preserving conditions for other sensors.
	 */
	const syncAllPresenceToAlarmObject = () => {
		const presenceDecimals = new Set(selectedPresenceDecimals);
		if (presenceDecimals.size === 0) return;

		const withoutSelectedPresence = (alarm_object.if.or ?? []).filter((e) => {
			const parts = e?.eq?.[0]?.rr?.split(':') ?? [];
			if (parts[0] !== 'dev') return true;
			return !presenceDecimals.has(parts[1]);
		});

		const additions: Array<{ eq: [{ rr: string }, string] }> = [];
		for (const dec of presenceDecimals) {
			const checked = checkedZonesByDevice[dec] ?? new Set();
			if (checked.size === 0) {
				additions.push({ eq: [{ rr: anchorRr(dec) }, 'true'] });
			} else {
				for (const zoneId of checked) {
					additions.push({
						eq: [{ rr: `dev:${dec}:${buildPresenceConditionName('st', zoneId)}` }, 'true'],
					});
				}
			}
		}

		alarm_object.if.or = [...withoutSelectedPresence, ...additions];
		alarm_object = alarm_object;
	};

	function availableZonesForSensor(sensorId: string): { id: string; title: string }[] {
		if (!sensorId || !isShellyPresence($devices[sensorId]?.type)) return [];

		const zones = getPresenceZones(sensorId, memberSettings);
		if (zones.length > 0) return zones.map((z) => ({ id: z.id, title: z.title }));

		const status = readDeviceStatus(sensorId);
		if (status) {
			const keys = Object.keys(status)
				.filter((k) => /^presencezone:\d+$/.test(k))
				.sort((a, b) => parseInt(a.split(':')[1]) - parseInt(b.split(':')[1]));
			if (keys.length > 0) {
				return keys.map((k) => {
					const id = k.split(':')[1];
					return {
						id,
						title: $format('label_zone_number', { default: 'Zone {n}', values: { n: id } }),
					};
				});
			}
		}

		return [{ id: '200', title: $format('presence_main_zone', { default: 'Main zone' }) }];
	}

	function isRadarActiveFor(sensorId: string): boolean {
		return memberSettings[real_id(sensorId)]?.presence?.enable !== false;
	}

	/** Single-zone devices: auto-pick the only zone once settings are available (matches legacy flow). */
	const runAutoSelectAllPresenceSensors = () => {
		if (!hasAnyPresence || !selectedPresenceDecimals.length) return;
		let changed = false;
		const nextChecked = { ...checkedZonesByDevice };
		for (const sensor of selectedPresenceSensors) {
			const sid = sensor?.id ?? '';
			const dec = sensorIdToDecimal(sid);
			if (!dec || !isRadarActiveFor(sid)) continue;
			const zones = availableZonesForSensor(sid);
			const checked = nextChecked[dec] ?? new Set();
			if (zones.length === 1 && !userDeselectedAllByDevice[dec] && checked.size === 0) {
				nextChecked[dec] = new Set([zones[0].id]);
				changed = true;
			}
		}
		if (changed) {
			checkedZonesByDevice = nextChecked;
			syncAllPresenceToAlarmObject();
		}
	};

	/** When the set of selected presence devices changes, re-read zone state from the alarm. */
	$: {
		const key = selectedPresenceDecimals.slice().sort().join('|');
		if (key !== presenceDecimalsInitKey) {
			presenceDecimalsInitKey = key;
			const nextChecked: Record<string, Set<string>> = {};
			const nextDeselected: Record<string, boolean> = {};
			for (const dec of selectedPresenceDecimals) {
				const existingZones = readAlarmZones(dec);
				nextChecked[dec] = existingZones;
				nextDeselected[dec] = hasAnchorCondition(dec) && existingZones.size === 0;
			}
			checkedZonesByDevice = nextChecked;
			userDeselectedAllByDevice = nextDeselected;
			syncAllPresenceToAlarmObject();
			runAutoSelectAllPresenceSensors();
		}
	}

	/** Re-run auto-select when per-sensor presence / zone settings load. */
	$: presenceZoneAutoKey = !hasAnyPresence
		? ''
		: selectedPresenceSensors
				.map((s) => {
					const id = s?.id ?? '';
					if (!id) return '';
					const rid = real_id(id);
					const zc = Object.keys(memberSettings[rid] ?? {}).filter((k) =>
						/^presencezone:\d+$/.test(k),
					).length;
					return `${id}:${memberSettings[rid]?.presence?.enable !== false ? 1 : 0}:${zc}`;
				})
				.join('|');

	$: if (presenceDecimalsInitKey && presenceZoneAutoKey) {
		runAutoSelectAllPresenceSensors();
	}

	const toggleZone = (decimal: string, zoneId: string) => {
		const prev = checkedZonesByDevice[decimal] ?? new Set<string>();
		const next = new Set(prev);
		if (next.has(zoneId)) {
			next.delete(zoneId);
			if (next.size === 0) {
				userDeselectedAllByDevice = { ...userDeselectedAllByDevice, [decimal]: true };
			}
		} else {
			next.add(zoneId);
			userDeselectedAllByDevice = { ...userDeselectedAllByDevice, [decimal]: false };
		}
		checkedZonesByDevice = { ...checkedZonesByDevice, [decimal]: next };
		syncAllPresenceToAlarmObject();
	};

	const listItemAvatar = (sensor: any) =>
		sensor?.image || sensor?.icon || sensor?.backgroundColor || 'fak fa-solid-shelly';
</script>

<Container padding="scrollPaddings">
	{#if !hasAnyPresence}
		<Text styleType="body" class="text-muted">
			{$format('alarm_zones_no_presence_sensor', {
				default:
					'No presence sensor selected. Select one in the previous step to configure zones.',
			})}
		</Text>
	{:else}
		<Text element="h2" class="mb-s">
			{$format('alarm_presence_zones_heading', { default: 'Presence detection zones' })}
		</Text>
		<Text styleType="body-small" class="mb-m">
			{$format('alarm_zones_hint', {
				default: 'Select which radar zones should trigger this alarm.',
			})}
		</Text>

		<Stack gap="m">
			{#each selectedPresenceSensors as sensor (sensor.id)}
				{@const sensorId = sensor?.id ?? ''}
				{@const decimal = sensorIdToDecimal(sensorId)}
				{@const zones = availableZonesForSensor(sensorId)}
				{@const radarOn = isRadarActiveFor(sensorId)}
				{@const checked = checkedZonesByDevice[decimal] ?? emptyCheckedSet}

				<div class="presence-zones-sensor-block">
					<ListItem
						class="mb-s"
						icon={listItemAvatar(sensor)}
						image={sensor?.image}
						title={sensor?.name ??
							$format('title_unknown_device', { default: 'Unknown Device' })}
						progress={{ percent: 0, showPercentBar: false }}
					/>

					{#if !radarOn}
						<Text styleType="body-small" class="mb-0">
							{$format('alarm_zones_radar_disabled', {
								default:
									'Radar detection is disabled on this device. The alarm will trigger on any detected presence.',
							})}
						</Text>
					{:else}
						{#each zones as zone (zone.id)}
							<Checkbox
								id="zone-checkbox-{decimal}-{zone.id}"
								class="mb-s"
								size="m"
								checked={checked.has(zone.id)}
								disabled={zones.length === 1}
								on:click={() => toggleZone(decimal, zone.id)}
							>
								{zone.title}
							</Checkbox>
						{/each}
					{/if}
				</div>
			{/each}
		</Stack>
	{/if}
</Container>
